import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { GetCampaignDto } from 'woofwoof-api';
import { campaignApi } from '../../../../api/campaign';
import { translateKey } from 'src/locales/translate';


export type Campaign = GetCampaignDto;

export interface CreateCampaignDto {
  name: string;
  description?: string;
  keywords?: string[];
  dailyBudget?: number;
  startDate?: string;
  endDate?: string;
  promoCode?: string;
}

export interface UpdateCampaignBudgetDto {
  dailyBudget: number;
  startDate: string;
  endDate: string;
}

const CAMPAIGNS_QUERY_KEY = 'campaigns';

export function useCampaigns(organizationId: string) {
  const { data: campaigns, isLoading, refetch } = useQuery<Campaign[]>({
    queryKey: [CAMPAIGNS_QUERY_KEY, organizationId],
    queryFn: async () => {
      const response = await campaignApi.campaignControllerFindByOrganization({
        organizationId,
      });
      return response.data;
    },
    enabled: !!organizationId,
  });

  return { campaigns, isLoading, refetch };
}

export function useCreateCampaign(organizationId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (data: CreateCampaignDto) => {
      const response = await campaignApi.campaignControllerCreate({
        organizationId,
        createCampaignDto: data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_QUERY_KEY, organizationId] });
      enqueueSnackbar(translateKey("hardcoded.kampania_zostala_utworzona"), { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || translateKey("hardcoded.blad_podczas_tworzenia_kampanii"), { variant: 'error' });
    },
  });
}

export function useUpdateCampaignBudget(campaignId: string, organizationId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (data: UpdateCampaignBudgetDto) => {
      const response = await campaignApi.campaignControllerUpdateBudget({
        campaignId,
        updateCampaignBudgetDto: data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_QUERY_KEY, organizationId] });
      enqueueSnackbar(translateKey("hardcoded.budzet_kampanii_zostal_zaktualizowany"), { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || translateKey("hardcoded.blad_podczas_aktualizacji_budzetu"), { variant: 'error' });
    },
  });
}

export function useGenerateKeywords(campaignId: string) {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async () => {
      const response = await campaignApi.campaignControllerGenerateKeywords({
        campaignId,
      });
      return response.data;
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || translateKey("hardcoded.blad_podczas_generowania_slow_kluczowych"), { variant: 'error' });
    },
  });
}

export function useGenerateKeywordsForOrganization(organizationId: string) {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async () => {
      const response = await campaignApi.campaignControllerGenerateKeywordsForOrganization({
        organizationId,
      });
      return response.data;
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || translateKey("hardcoded.blad_podczas_generowania_slow_kluczowych"), { variant: 'error' });
    },
  });
}

export function useUpdateAdUrl(campaignId: string, organizationId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (url: string) => {
      const response = await campaignApi.campaignControllerUpdateAdUrl({
        campaignId,
        updateAdUrlDto: { url },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_QUERY_KEY, organizationId] });
      enqueueSnackbar(translateKey("hardcoded.url_reklamy_zostal_zaktualizowany"), { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || translateKey("hardcoded.blad_podczas_aktualizacji_url_reklamy"), { variant: 'error' });
    },
  });
}

export function useInitiatePayment(campaignId: string) {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async () => {
      const response = await campaignApi.campaignControllerInitiatePayment({
        campaignId,
      });
      return response.data;
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || translateKey("hardcoded.blad_podczas_inicjowania_platnosci"), { variant: 'error' });
    },
  });
}

export function useCampaignAction(campaignId: string, organizationId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const pauseMutation = useMutation({
    mutationFn: async () => {
      const response = await campaignApi.campaignControllerPause({
        campaignId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_QUERY_KEY, organizationId] });
      enqueueSnackbar(translateKey("hardcoded.kampania_zostala_wstrzymana"), { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || translateKey("hardcoded.blad_podczas_wstrzymywania_kampanii"), { variant: 'error' });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: async () => {
      const response = await campaignApi.campaignControllerResume({
        campaignId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_QUERY_KEY, organizationId] });
      enqueueSnackbar(translateKey("hardcoded.kampania_zostala_wznowiona"), { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || translateKey("hardcoded.blad_podczas_wznawiania_kampanii"), { variant: 'error' });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const response = await campaignApi.campaignControllerCancel({
        campaignId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_QUERY_KEY, organizationId] });
      enqueueSnackbar(translateKey("hardcoded.kampania_zostala_anulowana"), { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || translateKey("hardcoded.blad_podczas_anulowania_kampanii"), { variant: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await campaignApi.campaignControllerDelete({
        campaignId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_QUERY_KEY, organizationId] });
      enqueueSnackbar(translateKey("hardcoded.kampania_zostala_usunieta"), { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || translateKey("hardcoded.blad_podczas_usuwania_kampanii"), { variant: 'error' });
    },
  });

  return {
    pause: pauseMutation.mutate,
    resume: resumeMutation.mutate,
    cancel: cancelMutation.mutate,
    delete: deleteMutation.mutate,
    isPausing: pauseMutation.isPending,
    isResuming: resumeMutation.isPending,
    isCancelling: cancelMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
